import { forwardRef, useState } from 'react';
import Tippy from '@tippyjs/react';
import type { TippyProps } from '@tippyjs/react';
import type { Placement } from 'tippy.js';
import ToolMenu from 'shared/overlays/ToolMenu';

interface MenuItem {
  icon: string;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  sub?: (item: JSX.Element) => JSX.Element;
}

interface TippyMenuButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const TippyMenuButton = forwardRef<HTMLButtonElement, TippyMenuButtonProps>(({ onClick }, ref) => {
  return (
    <button className="tippy-menu-button" ref={ref} onClick={onClick}>
      <i className="fa-regular fa-ellipsis-v" />
    </button>
  );
});

interface TippyMenuProps {
  offset?: [number, number];
  placement?: Placement;
  menu?: MenuItem[];
  trigger?: string;
  duration?: number;
  getReferenceClientRect?: TippyProps['getReferenceClientRect'];
  popperOptions?: TippyProps['popperOptions'];
  zIndex?: number;
  appendTo?: TippyProps['appendTo'];
}

const TippyMenu = ({
  offset = [0, 10],
  placement = 'bottom-end',
  menu = [],
  trigger = 'click',
  duration = 0,
  getReferenceClientRect,
  popperOptions,
  zIndex = 9,
  appendTo = () => document.body,
}: TippyMenuProps) => {
  const [visible, setVisible] = useState(false);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setVisible(true);
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMenuClick = () => {
    setVisible(false);
  };

  return (
    <Tippy
      interactive
      placement={placement}
      offset={offset}
      getReferenceClientRect={getReferenceClientRect}
      popperOptions={popperOptions}
      zIndex={zIndex}
      appendTo={appendTo}
      duration={duration}
      trigger={trigger}
      content={visible ? <ToolMenu menuItems={menu} onClick={handleMenuClick} /> : null}
    >
      <TippyMenuButton onClick={handleButtonClick} />
    </Tippy>
  );
};

export default TippyMenu;
